/* Revisies van een Offerte: bevroren standen in quote_versions, genummerd 1, 2, 3.
 *
 * De levende offerte is de werkversie van revisie `current_version`. Vastleggen
 * bevriest die werkversie: is revisie `current_version` nog niet bevroren, dan wordt
 * dat die revisie; anders komt er een nieuwe (`current_version + 1`) en krijgt het
 * offertenummer het revisienummer erachter (P33 → P33-2), zoals het CRM altijd deed.
 * Eén nummering voor offerte en bestand is een apart besluit (buiten ADR 0018).
 *
 * Een revisie kan één PDF in de projectmap hebben (migratie 097). */

import { OfferteFout, type OfferteClient, type OfferteKern } from './acties';

export interface Revisie {
  id: string;
  version_number: number;
  total_amount: number | null;
  notes: string | null;
  created_at: string;
  created_by: string | null;
  file_version_id: string | null;
  file_name: string | null;
  file_folder: string | null;
  file_web_url: string | null;
  file_saved_at: string | null;
  /** De omschrijving van de Versie in de projectmap (project_file_versions.message). */
  file_message?: string | null;
}

const BASIS = 'id, version_number, total_amount, notes, created_at, created_by';
const KOLOMMEN = `${BASIS}, file_version_id, file_name, file_folder, file_web_url, file_saved_at`;

const getal = (v: unknown) => (v === null || v === undefined || v === '' ? null : Number(v));

/** Nieuwste eerst. */
export async function haalRevisies(client: OfferteClient, quoteId: string): Promise<Revisie[]> {
  const vraag = (kolommen: string) =>
    client.from('quote_versions').select(kolommen).eq('quote_id', quoteId).order('version_number', { ascending: false });
  let { data, error } = await vraag(`${KOLOMMEN}, bestand:project_file_versions(message)`);
  // Zolang migratie 097 er niet is, bestaan de bestandskolommen nog niet: dan zonder.
  if (error?.code === '42703' || error?.code === 'PGRST200') ({ data, error } = await vraag(BASIS));
  if (error) throw error;
  return (data ?? []).map((r: any) => {
    const { bestand, ...rij } = r;
    return {
      file_version_id: null, file_name: null, file_folder: null, file_web_url: null, file_saved_at: null,
      ...rij,
      total_amount: getal(r.total_amount),
      file_message: (Array.isArray(bestand) ? bestand[0] : bestand)?.message ?? null,
    };
  });
}

/**
 * Het offertenummer zonder revisie: 'P33-2' bij revisie 2 is 'P33'. Alleen een
 * achtervoegsel dat gelijk is aan de huidige revisie telt; 'P28-3' bij revisie 1 is
 * een eigen offerte (de derde van P28), geen revisie 3.
 */
export function basisNummer(quoteNumber: string, currentVersion: number | null): string {
  const m = /^(.*)-(\d+)$/.exec(quoteNumber);
  if (m && currentVersion !== null && currentVersion > 1 && Number(m[2]) === currentVersion) return m[1]!;
  return quoteNumber;
}

export function nummerVoorRevisie(quoteNumber: string, currentVersion: number | null, revisie: number): string {
  const basis = basisNummer(quoteNumber, currentVersion);
  return revisie > 1 ? `${basis}-${revisie}` : basis;
}

export interface RevisieKeuze {
  /** De huidige revisie als die bevroren is maar nog geen bestand heeft: daar kan de PDF bij. */
  bestaand: Revisie | null;
  /** Nummer van de revisie die vastleggen oplevert. */
  nieuw: number;
  /** Wordt de nieuwe revisie de huidige (nog niet bevroren) of komt er één bij? */
  nieuwIsHuidige: boolean;
  standaard: 'bestaand' | 'nieuw';
}

/** Hoe lang na het bevriezen een wijziging nog als "dezelfde stand" telt (het nummer bijwerken, een mislukte poging). */
const ZELFDE_STAND_MS = 5 * 60_000;

/**
 * Waar een PDF in de projectmap bij hoort. Is de huidige revisie bevroren zonder
 * bestand en is de offerte sindsdien niet gewijzigd, dan ligt die revisie voor de
 * hand (bijvoorbeeld na een mislukte upload); anders een nieuwe revisie.
 */
export function revisieKeuze(
  q: Pick<OfferteKern, 'current_version'> & { updated_at?: string | null },
  revisies: Revisie[],
  /** Totaal van de regels zoals ze nu zijn; wijkt het af van de bevroren revisie, dan is de stand veranderd. */
  huidigBedrag?: number | null,
): RevisieKeuze {
  const huidig = q.current_version ?? 1;
  const bevroren = revisies.find((r) => r.version_number === huidig) ?? null;
  const hoogste = Math.max(huidig, ...revisies.map((r) => r.version_number));
  if (!bevroren) return { bestaand: null, nieuw: huidig, nieuwIsHuidige: true, standaard: 'nieuw' };
  const bestaand = bevroren.file_name ? null : bevroren;
  const anderBedrag = huidigBedrag != null && bevroren.total_amount != null && Math.abs(huidigBedrag - bevroren.total_amount) >= 0.01;
  const sindsdienGewijzigd = anderBedrag || (q.updated_at
    ? Date.parse(q.updated_at) > Date.parse(bevroren.created_at) + ZELFDE_STAND_MS
    : true);
  return { bestaand, nieuw: hoogste + 1, nieuwIsHuidige: false, standaard: bestaand && !sindsdienGewijzigd ? 'bestaand' : 'nieuw' };
}

export interface VastlegKern extends OfferteKern {
  body?: string | null;
  terms?: string | null;
}

/**
 * Bevriest de werkversie als revisie `nummer`: de regels, de bedragen en de tekst.
 * Hoort daar een nieuw offertenummer bij en is dat al van een andere offerte, dan
 * blijft het nummer staan.
 */
export async function legRevisieVast(client: OfferteClient, q: VastlegKern, nummer: number, notitie: string | null): Promise<Revisie> {
  const huidig = q.current_version ?? 1;
  if (nummer !== huidig && nummer !== huidig + 1) {
    throw new OfferteFout(`Revisie ${nummer} past niet na revisie ${huidig}.`);
  }
  const { data: gebruiker } = await client.auth.getUser();
  const { data: regels, error: regelFout } = await client
    .from('quote_line_items')
    .select('*')
    .eq('quote_id', q.id)
    .order('position');
  if (regelFout) throw regelFout;
  const som = (k: string) => (regels ?? []).reduce((t: number, r: any) => t + (Number(r[k]) || 0), 0);

  const { data: rij, error } = await client
    .from('quote_versions')
    .insert({
      quote_id: q.id,
      version_number: nummer,
      title: q.title,
      body: q.body ?? null,
      terms: q.terms ?? null,
      line_items: regels ?? [],
      subtotal_amount: som('subtotal'),
      vat_amount: som('vat_amount'),
      total_amount: som('total'),
      notes: notitie?.trim() || null,
      created_by: gebruiker.user?.id ?? null,
    })
    .select(BASIS)
    .single();
  if (error) {
    if (error.code === '23505') throw new OfferteFout(`Revisie ${nummer} bestaat al; laad de pagina opnieuw.`);
    throw error;
  }

  if (nummer !== huidig) {
    let nieuwNummer = nummerVoorRevisie(q.quote_number, huidig, nummer);
    const { data: bezet } = await client
      .from('quotes')
      .select('id')
      .eq('quote_number', nieuwNummer)
      .neq('id', q.id)
      .is('deleted_at', null)
      .maybeSingle();
    if (bezet) nieuwNummer = q.quote_number;
    const { error: updateFout } = await client
      .from('quotes')
      .update({ current_version: nummer, quote_number: nieuwNummer })
      .eq('id', q.id);
    if (updateFout) throw updateFout;
  }
  // Net vastgelegd, dus nog zonder bestand (dat koppelt link_quote_version_file).
  return {
    ...rij,
    total_amount: getal(rij.total_amount),
    file_version_id: null, file_name: null, file_folder: null, file_web_url: null, file_saved_at: null, file_message: null,
  };
}

export interface VerloopPunt {
  revisie: Revisie;
  /** Verschil met de vorige revisie; null bij de eerste of zonder bedrag. */
  verschil: number | null;
}

/** Bedragverloop, oudste eerst, met het verschil per revisie. */
export function bedragVerloop(revisies: Revisie[]): VerloopPunt[] {
  const op = [...revisies].sort((a, b) => a.version_number - b.version_number);
  return op.map((r, i) => {
    const vorige = op[i - 1];
    const verschil = vorige && vorige.total_amount !== null && r.total_amount !== null ? r.total_amount - vorige.total_amount : null;
    return { revisie: r, verschil };
  });
}

/** De hele bevroren revisie (regels, tekst), om te bekijken of terug te zetten. */
export async function haalVolledigeRevisie(client: OfferteClient, id: string): Promise<Record<string, any>> {
  const { data, error } = await client.from('quote_versions').select('*').eq('id', id).single();
  if (error) throw error;
  return data;
}
